/**
 * Dashboard Service
 * Service for dashboard overview and metrics
 */

import { api } from './api'

export interface DashboardOverviewData {
  bots: {
    total: number
    active: number
    paused: number
    stopped: number
    error: number
  }
  apiKeys: {
    total: number
    active: number
    invalid: number
    revoked: number
  }
  activity: {
    activeOffers: number
    activeLoans: number
  }
  financial: {
    totalLent: number
    totalEarned: number
    earnings30d: number
    averageAPR: number
  }
  wallets: Array<{
    currency: string
    balance: number
    lent: number
  }>
  recentActivities: Array<{
    bot_id: number
    bot_name: string
    currency: string
    status: string
    last_cycle_at: number | null
    error_message: string | null
  }>
}

export interface DashboardOverview {
  success: boolean
  data: DashboardOverviewData
}

export interface CurrencyDashboard {
  currency: string
  stats: {
    totalBots: number
    activeBots: number
    totalLent: number
    totalEarned: number
    averageAPR: number
  }
  wallet: {
    balance: number
    activeOffers: number
    activeLoans: number
    lastUpdated: number
  } | null
  bots: Array<{
    id: number
    name: string
    status: string
    config: any
    totalLent: number
    totalEarned: number
    lastCycleAt: number | null
    errorMessage: string | null
    createdAt: number
  }>
}

export interface HealthStatus {
  healthy: boolean
  services: {
    database: 'healthy' | 'unhealthy'
    cache: 'healthy' | 'unhealthy'
    api: 'healthy' | 'unhealthy'
  }
  timestamp: number
}

/**
 * Dashboard Service
 * With ETag caching support for efficient data transfer
 */
export const dashboardService = {
  /**
   * Get dashboard overview (with ETag caching)
   */
  async getOverview(): Promise<DashboardOverview> {
    const response = await api.get<DashboardOverview>(
      '/dashboard/overview',
      undefined,
      true // Enable ETag support
    )
    return response
  },

  /**
   * Get currency-specific dashboard (with ETag caching)
   */
  async getCurrencyDashboard(currency: string): Promise<CurrencyDashboard> {
    const response = await api.get<{ success: boolean; data: CurrencyDashboard }>(
      `/dashboard/currency/${currency}`,
      undefined,
      true // Enable ETag support
    )
    return response.data
  },

  /**
   * Get system health status
   */
  async getHealth(): Promise<HealthStatus> {
    const response = await api.get<HealthStatus>('/dashboard/health')
    return response
  },
}
